import { useMemo, useState } from 'react';
import { TextField, InputAdornment, Box, Popover, IconButton } from '@mui/material';
import { HexColorPicker } from 'react-colorful';
import { ColorIcon } from 'src/assets/icons/ColorIcon';
import { debounce } from 'lodash';

type Props = {
  color: string;
  onChangeHandler: (key: string, value: string) => void;
  name: string;
  label?: string;
};

export const ColorPickerInput = ({ color, onChangeHandler, name, label }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);

  const debouncedChange = useMemo(
    () => debounce((newColor: string) => onChangeHandler(name, newColor), 50),
    [onChangeHandler, name]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        size="small"
        value={color}
        label={label ?? 'Text'}
        name="text"
        onChange={(e) => onChangeHandler(name, e.target.value)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ mr: -1.1 }}>
              <IconButton onClick={handleOpen} edge="end">
                <Box sx={{ p: '0.25rem', borderRadius: '8px', background: color }}>
                  <ColorIcon />
                </Box>
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2 }}>
          <HexColorPicker color={color} onChange={debouncedChange} />
        </Box>
      </Popover>
    </Box>
  );
};
